import React, { useState, Fragment, useEffect } from "react";
import {
  useNavigate,
  useSearchParams,
  Link as RouterLink,
} from "react-router-dom";
import {
  Container,
  Box,
  CssBaseline,
  TextField,
  Button,
  Snackbar,
  Alert,
  Typography,
  Grid,
} from "@mui/material";
import { SignInStatus } from "./states";
import { pushDataLayerEvent } from "./analytics";
import {
  verifyEmailAction,
  verifyPasswordResetCode,
  resetPasswordAction,
  login,
  autoLogin,
} from "./services/authService";

interface Props {
  continueUrl: string;
  setSignInStatus: (signInStatus: SignInStatus) => void;
  onRefreshStatus: () => void;
  oobCode: string;
  signInStatus: SignInStatus;
  autoLoginToken: string | null;
}

const VerifyEmail = ({
  oobCode,
  continueUrl,
  setSignInStatus,
  onRefreshStatus,
  signInStatus,
  autoLoginToken,
}: Props) => {
  const [failed, setFailed] = useState(false);
  const [verified, setVerified] = useState(false);
  const [autoLoginAttempted, setAutoLoginAttempted] = useState(false);
  const navigate = useNavigate();

  // 自動ログインを試みる（autoLoginToken がある場合）
  useEffect(() => {
    const tryAutoLogin = async () => {
      if (
        autoLoginToken &&
        signInStatus === SignInStatus.NOTSIGNEDIN &&
        !autoLoginAttempted
      ) {
        setAutoLoginAttempted(true);
        const result = await autoLogin(autoLoginToken);
        if (result.ok) {
          // 自動ログイン成功、認証状態を更新
          await onRefreshStatus();
        }
        // 失敗しても続行（手動ログインフォームを表示）
      }
    };
    tryAutoLogin();
  }, [autoLoginToken, signInStatus, autoLoginAttempted, onRefreshStatus]);

  useEffect(() => {
    const verifyEmail = async () => {
      const result = await verifyEmailAction(oobCode);
      if (result.ok) {
        // メール確認成功、認証状態を更新
        await onRefreshStatus();
        setVerified(true);

        pushDataLayerEvent("verify");
      } else {
        setFailed(true);
      }
    };
    verifyEmail();
  }, [oobCode, onRefreshStatus]);

  // 認証成功後、ログイン状態を確認する
  useEffect(() => {
    if (
      verified &&
      signInStatus !== SignInStatus.NOTSIGNEDIN &&
      signInStatus !== SignInStatus.INITIALSTATE
    ) {
      // ログイン済み、購入ページへ移動
      if (continueUrl) {
        const url = new URL(continueUrl);
        const params = url.searchParams;
        let plan = params.get("plan");
        setSignInStatus(SignInStatus.PURCHASING);
        navigate("/signup?plan=" + plan);
      }
    }
  }, [verified, signInStatus, continueUrl, setSignInStatus, navigate]);

  if (failed) {
    return (
      // @ts-ignore
      <Container component="main" maxWidth="xs" align="center" sx={{ mt: 4 }}>
        验证失败
      </Container>
    );
  }

  // 認証は成功したがログインしていないため、ログインフォームを表示する
  if (verified && signInStatus === SignInStatus.NOTSIGNEDIN) {
    // continueUrl から plan を抽出
    let plan: string | null = null;
    if (continueUrl) {
      try {
        const url = new URL(continueUrl);
        plan = url.searchParams.get("plan");
      } catch (e) {
        // ignore
      }
    }

    return (
      <VerifyLoginForm
        plan={plan}
        onRefreshStatus={onRefreshStatus}
        setSignInStatus={setSignInStatus}
      />
    );
  }

  return (
    // @ts-ignore
    <Container component="main" maxWidth="xs" align="center" sx={{ mt: 4 }}>
      正在验证...
    </Container>
  );
};

interface VerifyLoginFormProps {
  plan: string | null;
  onRefreshStatus: () => void;
  setSignInStatus: (signInStatus: SignInStatus) => void;
}

const VerifyLoginForm = ({
  plan,
  onRefreshStatus,
  setSignInStatus,
}: VerifyLoginFormProps) => {
  const [showSnack, setShowSnack] = useState(false);
  const [snackMessage, setSnackMessage] = useState<string>();
  const navigate = useNavigate();

  const handleLogin = async (e: any) => {
    e.preventDefault();
    const email = e.target.email.value;
    const password = e.target.password.value;

    const result = await login(email, password);

    if (result.ok) {
      await onRefreshStatus();
      setSignInStatus(SignInStatus.PURCHASING);
      navigate("/signup?plan=" + plan);
    } else {
      setSnackMessage("登录失败");
      setShowSnack(true);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h5"
          component="h1"
          gutterBottom
          fontWeight="fontWeightBold"
          color="success.main"
        >
          邮箱验证成功
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ mb: 2 }}
        >
          请登录以继续，或如果您在其他浏览器中打开了此链接，请返回原浏览器刷新页面。
        </Typography>
        <Box
          component="form"
          noValidate
          onSubmit={handleLogin}
          sx={{ mt: 1, width: "100%" }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="email"
                label="电子邮件地址"
                name="email"
                autoComplete="email"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="password"
                label="密码"
                type="password"
                id="password"
                autoComplete="password"
              />
            </Grid>
          </Grid>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
          >
            登录
          </Button>
        </Box>
      </Box>
      <Snackbar
        open={showSnack}
        autoHideDuration={6000}
        onClose={() => setShowSnack(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSnack(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {snackMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

interface ResetPasswordProps {
  oobCode: string;
}

const ResetPassword = (props: ResetPasswordProps) => {
  const [showSnack, setShowSnack] = useState(false);
  const [snackMessage, setSnackMessage] = useState<any>();
  const [failed, setFailed] = useState(false);
  const [succeed, setSucceed] = useState(false);
  const [email, setEmail] = useState<string>();
  useEffect(() => {
    const verifyCode = async () => {
      const result = await verifyPasswordResetCode(props.oobCode);
      if (result.ok && result.email) {
        setEmail(result.email);
      } else {
        setFailed(true);
      }
    };
    verifyCode();
  }, [props.oobCode]);
  if (failed) {
    return (
      // @ts-ignore
      <Container component="main" maxWidth="xs" align="center" sx={{ mt: 4 }}>
        密码重置链接已失效
      </Container>
    );
  }
  if (succeed) {
    return (
      // @ts-ignore
      <Container component="main" maxWidth="xs" align="center">
        <Typography
          component="h2"
          variant="body1"
          color="text.primary"
          align="center"
          sx={{ mt: 4 }}
        >
          密码重置成功
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "baseline",
          }}
        >
          <Button
            variant="text"
            to="/login"
            sx={{ mt: 4 }}
            component={RouterLink}
          >
            登录
          </Button>
        </Box>
      </Container>
    );
  }
  if (!email) {
    return (
      // @ts-ignore
      <Container component="main" maxWidth="xs" align="center" sx={{ mt: 4 }}>
        验证中的密码设置...
      </Container>
    );
  }
  const handleReset = async (e: any) => {
    e.preventDefault();
    const password = e.target.password.value;
    const result = await resetPasswordAction(props.oobCode, password);
    if (result.ok) {
      setSucceed(true);
    } else if (result.error === "weak-password") {
      setSnackMessage("密码强度不足");
      setShowSnack(true);
    } else {
      setSnackMessage("发生错误");
      setShowSnack(true);
    }
  };
  return (
    <Container component="main" maxWidth="sm">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          fontWeight="fontWeightBold"
        >
          重置密码
        </Typography>
        <Typography
          variant="body1"
          component="h1"
          gutterBottom
          fontWeight="fontWeightBold"
        >
          {email}
        </Typography>
        <Box
          component="form"
          noValidate
          onSubmit={handleReset}
          sx={{ mt: 3, width: "100%" }}
        >
          <TextField
            required
            fullWidth
            name="password"
            label="密码"
            helperText="至少 6 个字符，字母和数字组合"
            type="password"
            id="password"
            autoComplete="new-password"
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
          >
            确认
          </Button>
        </Box>
      </Box>
      <Snackbar
        open={showSnack}
        autoHideDuration={6000}
        onClose={() => setShowSnack(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSnack(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {snackMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

interface VerifyProps {
  signInStatus: SignInStatus;
  setSignInStatus: (signInStatus: SignInStatus) => void;
  onRefreshStatus: () => void;
}

const Verify = (props: VerifyProps) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const mode = searchParams.get("mode");
  const oobCode = searchParams.get("oobCode");
  const continueUrl = searchParams.get("continueUrl");
  const autoLoginToken = searchParams.get("autoLoginToken");
  if (!mode) {
    return (
      // @ts-ignore
      <Container component="main" maxWidth="xs" align="center" sx={{ mt: 4 }}>
        无效请求。
      </Container>
    );
  }
  if (props.signInStatus === SignInStatus.INITIALSTATE) {
    return (
      // @ts-ignore
      <Container component="main" maxWidth="xs" align="center" sx={{ mt: 4 }}>
        <CssBaseline />
      </Container>
    );
  }
  // すでに verify 済み（PURCHASING/PREMIUM）の場合
  if (
    mode === "verifyEmail" &&
    [SignInStatus.PURCHASING, SignInStatus.PREMIUM].includes(props.signInStatus)
  ) {
    // continueUrl があればそこにリダイレクト
    if (continueUrl) {
      try {
        const url = new URL(continueUrl);
        const plan = url.searchParams.get("plan");
        // useEffect の代わりに即座にリダイレクト
        navigate("/signup?plan=" + plan);
        return (
          // @ts-ignore
          <Container
            component="main"
            maxWidth="xs"
            align="center"
            sx={{ mt: 4 }}
          >
            正在跳转...
          </Container>
        );
      } catch (e) {
        // ignore
      }
    }
    // continueUrl がなければ「已完成验证」と表示
    return (
      // @ts-ignore
      <Container component="main" maxWidth="xs" align="center" sx={{ mt: 4 }}>
        已完成验证
      </Container>
    );
  }
  return (
    <Fragment>
      {/* @ts-ignore */}
      {mode === "resetPassword" && <ResetPassword oobCode={oobCode} />}
      {mode === "verifyEmail" && (
        <VerifyEmail
          // @ts-ignore
          oobCode={oobCode}
          // @ts-ignore
          continueUrl={continueUrl}
          setSignInStatus={props.setSignInStatus}
          onRefreshStatus={props.onRefreshStatus}
          signInStatus={props.signInStatus}
          autoLoginToken={autoLoginToken}
        />
      )}
    </Fragment>
  );
};

export default Verify;
